import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBabyStore } from '@/stores/useBabyStore';
import { Disclaimer } from '@/components/ui/Disclaimer';
import { getPriceDisplay } from '@/i18n/locale';

// ════════════════════════════════════════════════════════════════════
// LandingDE — German version of the landing page.
//
// Mirrors Landing.tsx structure 1:1; only the strings change. We keep
// the parallel-file approach (rather than i18n string maps) for v1
// because:
//   · the English Landing is working and shouldn't be touched
//   · German copy needs care in many places; in-context editing is
//     much easier to review than a flat strings.ts
//   · future translators can copy this file as a template
//
// Tone notes:
//   · "Du" form throughout — German mums in WhatsApp groups speak
//     informally to each other; Alma is "wie eine Freundin"
//   · German quotation marks („…")
//   · Comma as decimal separator (handled via getPriceDisplay)
//   · Trust strip swaps US bodies (AAP/CDC) for German equivalents:
//     DGKJ (pediatricians), STIKO (vaccines), RKI (public health),
//     WHO (international growth standards)
// ════════════════════════════════════════════════════════════════════

export default function LandingDE() {
  const { user, signOut } = useAuthStore();
  const resetBabies = useBabyStore((s) => s.reset);
  const navigate = useNavigate();
  const isAuthed = Boolean(user);

  const onSignOut = async () => {
    await signOut();
    resetBabies();
  };

  return (
    <div className="min-h-[100dvh] bg-cream">
      <nav className="relative z-20 mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-display font-semibold">A</span>
          </div>
          <span className="font-display text-xl font-semibold text-slate tracking-tight">
            Alma
          </span>
        </Link>
        <div className="flex items-center gap-2">
          {isAuthed ? (
            <>
              <button onClick={onSignOut} className="btn-ghost text-sm">
                Abmelden
              </button>
              <button onClick={() => navigate('/today')} className="btn-primary text-sm">
                Weiter
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm">
                Anmelden
              </Link>
              <Link to="/start" className="btn-primary text-sm">
                Loslegen
              </Link>
            </>
          )}
        </div>
      </nav>

      <Hero isAuthed={isAuthed} />
      <MumQuestions isAuthed={isAuthed} />
      <TrustStrip />
      <DramaticMoment />
      <Coverage />
      <AgeTuned />
      <ProductSurfaces />
      <Pricing isAuthed={isAuthed} />
      <FAQ />
      <FinalCta isAuthed={isAuthed} />

      <footer className="relative mx-auto max-w-6xl px-6 py-10 border-t border-primary-50">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-muted">
          <div>© {new Date().getFullYear()} Alma · Kein Ersatz für professionellen ärztlichen Rat.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-slate">Datenschutz</Link>
            <Link to="/imprint" className="hover:text-slate">Impressum</Link>
          </div>
        </div>
        <div className="mt-6">
          <Disclaimer className="text-center text-slate-muted text-[11px] leading-relaxed max-w-2xl mx-auto" />
        </div>
      </footer>
    </div>
  );
}

function Hero({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70rem 45rem at 85% -10%, rgba(232,164,160,0.18) 0%, transparent 60%), radial-gradient(60rem 40rem at -10% 110%, rgba(143,181,158,0.18) 0%, transparent 55%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 pt-12 pb-20 sm:pt-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-primary-100 px-3 py-1 text-xs text-primary-700 mb-6 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              Die ersten 24 Monate deines Babys
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-[68px] font-semibold tracking-tight text-slate leading-[1.02]">
              Jede <span className="italic text-primary">„Ist das normal?"</span>-Frage,
              <br />
              beantwortet wie von einer Freundin.
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-muted leading-relaxed max-w-xl">
              Ruhige, altersgenaue Antworten — zu Schlafen, Stillen, Hautausschlägen,
              Weinen, Meilensteinen und „Soll ich zur Ärztin?"-Momenten. Basierend auf
              pädiatrischen Leitlinien, abgestimmt auf die genaue Lebenswoche deines Babys.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {isAuthed ? (
                <Link to="/today" className="btn-primary text-base px-6 py-3.5 text-center shadow-sm">
                  Weiter zu deinem Tag →
                </Link>
              ) : (
                <>
                  <Link
                    to="/start"
                    className="btn-primary text-base px-6 py-3.5 text-center shadow-sm hover:shadow-md transition-shadow"
                  >
                    Alma kostenlos fragen →
                  </Link>
                  <Link
                    to="/login"
                    className="btn-ghost text-base px-6 py-3.5 text-center"
                  >
                    Ich habe schon ein Konto
                  </Link>
                </>
              )}
            </div>

            {!isAuthed && (
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-slate-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Basiert auf pädiatrischen Leitlinien
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Altersgenaue Antworten
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Klare Warnzeichen
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-primary">✓</span>Daten geschützt
                </span>
              </div>
            )}
          </div>

          <div className="lg:col-span-6 flex justify-center lg:justify-end">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneMockup() {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-8 rounded-[60px] opacity-40 blur-2xl"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(232,164,160,0.4), rgba(143,181,158,0.3) 40%, transparent 70%)',
        }}
      />

      <div className="relative w-[300px] sm:w-[340px] rounded-[44px] bg-slate p-2 shadow-2xl ring-1 ring-slate/10">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate rounded-b-2xl z-10" />
        <div className="relative rounded-[36px] bg-cream overflow-hidden h-[640px] sm:h-[680px]">
          <div className="px-7 pt-3 pb-2 flex items-center justify-between text-[11px] text-slate font-medium">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <span>●●●●</span>
            </div>
          </div>

          <div className="px-4 py-3 border-b border-primary-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
                <span className="text-white text-[10px] font-display font-semibold">A</span>
              </div>
              <span className="font-display text-sm font-semibold text-slate">Alma</span>
            </div>
            <div className="text-[10px] text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full">
              Maya · 7 Wochen
            </div>
          </div>

          <div className="px-3 mt-4 flex justify-end">
            <div className="max-w-[80%] rounded-2xl rounded-br-md bg-primary text-white px-3 py-2 text-[12px] leading-relaxed">
              Mein 7 Wochen altes Baby weint jeden Abend gegen 19 Uhr — ist das normal?
            </div>
          </div>

          <div className="px-3 mt-3">
            <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white border border-primary-50 shadow-sm px-3 py-2.5 text-[11px] leading-relaxed text-slate">
              <p>
                Ja — das ist eine der häufigsten Beobachtungen in den ersten Wochen.
                Abendliche Unruhe erreicht ihren Höhepunkt bei fast allen Babys zwischen
                der 6. und 8. Lebenswoche und klingt meist zwischen dem 3. und 4. Monat
                wieder ab.
              </p>
              <p className="mt-2">
                Was den meisten Eltern hilft: ein ruhiges Runterkommen vor der unruhigen
                Phase, Tragen in sanfter Bewegung (Tragetuch, leichtes Wiegen), weißes
                Rauschen, gedämpftes Licht.
              </p>
              <p className="mt-2 text-slate-muted">
                Ruf deine Kinderärztin an, wenn Fieber, Erbrechen oder mehrstündiges
                Untröstlich-Sein dazukommen — sonst liegt das gut im normalen Bereich.
              </p>
            </div>
          </div>

          <div className="absolute bottom-4 left-3 right-3 rounded-full bg-white border border-primary-100 px-4 py-2.5 text-[11px] text-slate-muted shadow-sm">
            Frag Alma alles…
          </div>
        </div>
      </div>
    </div>
  );
}

function MumQuestions({ isAuthed }: { isAuthed: boolean }) {
  // Same 6 questions as English but translated. Each tap opens /start
  // (or /chat if authed) with the question as ?prompt= so it's
  // auto-sent after the 15-second onboarding.
  const questions = [
    { q: 'Warum grunzt mein Neugeborenes?', age: '0–4 Wochen' },
    { q: 'Ist grüner Stuhl normal?', age: '0–6 Monate' },
    { q: 'Warum weint mein Baby jeden Abend?', age: '6–12 Wochen' },
    { q: 'Wie viel sollte mein 3 Monate altes Baby schlafen?', age: '3 Monate' },
    { q: 'Ist dieser Ausschlag dringend?', age: 'Jedes Alter' },
    { q: 'Ab wann ist Fieber bedenklich?', age: 'Jedes Alter' },
  ];

  const linkFor = (q: string) => {
    const param = encodeURIComponent(q);
    return isAuthed ? `/chat?prompt=${param}` : `/start?prompt=${param}`;
  };

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 pt-2 pb-16 sm:pt-4 sm:pb-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Probier eine echte Frage
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate tracking-tight leading-tight">
            Die Fragen, die <span className="italic text-primary">jede Mutter</span> am Ende googelt.
          </h2>
          <p className="mt-4 text-base text-slate-muted">
            Tippe auf eine — Alma antwortet sofort. Keine Anmeldung nötig.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {questions.map((item) => (
            <Link
              key={item.q}
              to={linkFor(item.q)}
              className="group rounded-2xl border border-primary-100 bg-white/80 hover:bg-white hover:border-primary-200 hover:shadow-sm transition-all p-5 text-left"
            >
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-muted">
                {item.age}
              </div>
              <div className="mt-2 font-display text-[15px] sm:text-base text-slate leading-snug">
                {item.q}
              </div>
              <div className="mt-3 text-[12px] text-primary-700 font-medium inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Alma fragen
                <span aria-hidden>→</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  // German equivalents of US medical bodies:
  //   DGKJ = Deutsche Gesellschaft für Kinder- und Jugendmedizin
  //   STIKO = Ständige Impfkommission (RKI)
  //   RKI = Robert Koch-Institut (German public health institute)
  //   WHO = international, name unchanged
  return (
    <section className="relative border-y border-primary-50 bg-white/40 backdrop-blur">
      <div className="mx-auto max-w-6xl px-6 py-6 sm:py-8 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 items-center text-center">
        <div>
          <div className="font-display text-2xl font-semibold text-slate">DGKJ</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">Leitlinien</div>
        </div>
        <div>
          <div className="font-display text-2xl font-semibold text-slate">STIKO</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">Impfplan</div>
        </div>
        <div>
          <div className="font-display text-2xl font-semibold text-slate">WHO</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">Wachstumskurven</div>
        </div>
        <div>
          <div className="font-display text-2xl font-semibold text-slate">112</div>
          <div className="text-[11px] text-slate-muted uppercase tracking-wide mt-1">
            Begleit-Karten
          </div>
        </div>
      </div>
    </section>
  );
}

function DramaticMoment() {
  // German fever scenario — uses 38.0°C (≈100.4°F, the AAP threshold)
  // and references DGKJ/Kinderärztin (German pediatrician) instead of
  // AAP/pediatrician. Emergency number 112 instead of 911.
  const steps = [
    {
      time: '0s',
      title: 'Es ist 23 Uhr und das Thermometer zeigt 38,5 °C',
      body: 'Dein 3 Wochen altes Baby fühlt sich heiß an. Du bist allein und zweifelst an allem. Statt zu googeln öffnest du Alma.',
      tone: 'parent',
    },
    {
      time: '3s',
      title: 'Alma erkennt: das ist ein Notfall',
      body: 'Fieber ab 38,0 °C bei einem Baby unter 3 Monaten gilt laut DGKJ als automatischer Notfall. Alma weiß das — eine fest verdrahtete Regel, keine KI-Schätzung.',
      tone: 'system',
    },
    {
      time: '5s',
      title: 'Ein rotes Banner erscheint',
      body: '„Das könnte ein Notfall sein — Ruf 112 oder fahre sofort in die Notaufnahme." Keine Verharmlosung. Kein Zögern.',
      tone: 'alert',
    },
    {
      time: '8s',
      title: 'Du liest die Erklärung. Du rufst an.',
      body: 'Alma erklärt dir, was in der Notaufnahme passieren wird (Blut- und Urinuntersuchung), damit dich nichts überrascht. Du greifst zur Wickeltasche.',
      tone: 'parent',
    },
  ];

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Wenn es wirklich darauf ankommt
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Alma weiß, wann sie dich beruhigt —
            <br />
            <span className="italic text-primary">und wann sie dich zur Ärztin schickt.</span>
          </h2>
          <p className="mt-5 text-base text-slate-muted leading-relaxed">
            Die meisten Fragen sind harmlos. Einige sind dringend. So sieht es aus,
            wenn das Thermometer um 23 Uhr 38,5 °C anzeigt.
          </p>
        </div>

        <div className="relative">
          <div
            aria-hidden
            className="absolute left-7 top-7 bottom-7 w-px bg-gradient-to-b from-primary-200 via-primary-100 to-transparent hidden sm:block"
          />

          <div className="space-y-5 sm:space-y-7">
            {steps.map((step, i) => {
              const dotColor =
                step.tone === 'alert'
                  ? 'bg-danger text-white'
                  : step.tone === 'system'
                    ? 'bg-primary text-white'
                    : 'bg-primary-50 text-primary-700 border border-primary-100';
              return (
                <div key={i} className="relative flex gap-4 sm:gap-5">
                  <div className="shrink-0 relative z-10">
                    <div
                      className={
                        'w-14 h-14 rounded-2xl flex items-center justify-center font-display font-semibold text-sm shadow-sm ' +
                        dotColor
                      }
                    >
                      {step.time}
                    </div>
                  </div>
                  <div
                    className={
                      'flex-1 rounded-2xl border p-5 transition-shadow ' +
                      (step.tone === 'alert'
                        ? 'border-danger/30 bg-danger/5'
                        : 'border-primary-50 bg-white shadow-sm')
                    }
                  >
                    <h3 className="font-display font-semibold text-slate text-lg leading-snug">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[15px] text-slate-muted leading-relaxed">{step.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-slate-muted italic">
            Das ist die tatsächliche Antwort des echten Produkts. Keine Marketing-Annäherung.
          </p>
        </div>
      </div>
    </section>
  );
}

function Coverage() {
  const topics = [
    { icon: '🤱', title: 'Stillen & Füttern', body: 'Anlegen, Flasche, Beikost, Allergene, mäkeliges Essen.' },
    { icon: '🌙', title: 'Schlaf', body: 'Rhythmen, Schlafregressionen, Schlaftraining-Methoden.' },
    { icon: '🩺', title: 'Gesundheit', body: 'Fieber, Ausschlag, wann Anruf vs. Notaufnahme. Triage mit DGKJ-Schwellen.' },
    { icon: '🌱', title: 'Meilensteine', body: 'Entwicklungsschritte, worauf achten, wann zur Kinderärztin.' },
    { icon: '💛', title: 'Stimmung nach der Geburt', body: 'Wochenbettdepression erkennen, sanftes Screening, Beratungsstellen.' },
    { icon: '💉', title: 'Impfungen', body: 'STIKO-Impfplan, was bei jedem Termin passiert, was du fragen kannst.' },
    { icon: '🍽', title: 'Ernährung', body: 'Altersgenaue Essenspläne mit Gramm. 7 Rezepte pro Woche.' },
    { icon: '🦷', title: 'Zahnen', body: 'Wann welcher Zahn kommt. Wie es sich tatsächlich anfühlt.' },
  ];

  return (
    <section className="relative bg-white/60 border-y border-primary-50">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Was Alma abdeckt
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Die kompletten ersten <span className="italic text-primary">zwei Jahre.</span>
          </h2>
          <p className="mt-5 text-base text-slate-muted leading-relaxed">
            Kein simpler Chatbot. Ein wachsender Begleiter mit Wochen-Karten,
            altersgenauer Ernährung und evidenzbasierten Antworten auf alles, was kommt.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {topics.map((t) => (
            <div
              key={t.title}
              className="rounded-2xl border border-primary-50 bg-cream/60 hover:bg-white hover:shadow-sm transition-all p-5"
            >
              <div className="text-3xl">{t.icon}</div>
              <div className="font-display font-semibold text-slate mt-3">{t.title}</div>
              <div className="text-[13px] text-slate-muted mt-1.5 leading-relaxed">{t.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AgeTuned() {
  const bands = [
    {
      label: '0–6 Monate',
      tag: 'Neugeborenes',
      title: 'Die erschöpften Wochen',
      body: 'Stillschwierigkeiten, Cluster-Feeding, der Wachstumsschub mit 6 Wochen, sicheres Schlafen, die „Ist das Drei-Monats-Koliken?"-Frage. Der richtige Ton für jemanden, der nicht geschlafen hat.',
      bg: 'from-primary-50 to-cream',
    },
    {
      label: '6–12 Monate',
      tag: 'Erste Beikost',
      title: 'Brei, Sitzen und plötzliche Zähnchen',
      body: 'Eisenreiche Beikost, Allergene früh einführen (LEAP-Studie), Würgen vs. Verschlucken, die 8-Monats-Schlafregression, erste Backenzähne, Höhepunkt der Trennungsangst.',
      bg: 'from-accent/15 to-cream',
    },
    {
      label: '12–24 Monate',
      tag: 'Kleinkind',
      title: 'Wörter, Meinungen und Essensstreiks',
      body: 'Flasche abgewöhnen, Wortexplosion, Vorsorgeuntersuchungen, Trotzanfälle erklärt, mäkeliges Essen ohne Kampf, Bildschirmzeit, Sauberkeitsentwicklung.',
      bg: 'from-primary-50 to-accent/10',
    },
  ];

  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Für jedes Alter gebaut
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Ratschläge für ein 11 Wochen altes Baby
            <br />
            <span className="italic text-primary">sind nicht dieselben wie für ein 14 Wochen altes.</span>
          </h2>
          <p className="mt-5 text-base text-slate-muted leading-relaxed">
            Alma gibt keine generischen Tipps. Jede Antwort ist auf die genaue Lebenswoche
            deines Babys abgestimmt — weil die Entwicklung es auch ist.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {bands.map((band) => (
            <div
              key={band.label}
              className={
                'rounded-2xl border border-primary-50 p-6 bg-gradient-to-br ' + band.bg
              }
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold">
                <span className="text-primary-700">{band.label}</span>
                <span className="text-slate-muted/40">·</span>
                <span className="text-slate-muted">{band.tag}</span>
              </div>
              <h3 className="font-display text-xl font-semibold text-slate mt-3 leading-snug">
                {band.title}
              </h3>
              <p className="mt-2.5 text-sm text-slate leading-relaxed">{band.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductSurfaces() {
  const surfaces = [
    {
      eyebrow: 'Frag alles',
      title: 'Chat',
      body: 'Tippen oder fragen. Antworten wie von einer Freundin, die das genaue Alter deines Babys kennt. Notfall-Erkennung mit roten Warnbannern. Hinweise ohne Juristerei.',
      visual: <ChatPreview />,
    },
    {
      eyebrow: 'Auf deine Woche abgestimmt',
      title: 'Diese Woche',
      body: '112 evidenzbasierte Begleit-Karten über 24 Monate. Vergangen, jetzt, demnächst — damit du vorbereitet bist, nicht überrascht.',
      visual: <CardPreview />,
    },
    {
      eyebrow: 'Altersgenau essen',
      title: 'Ernährung',
      body: '7 echte Rezepte pro Woche mit Gramm und Haushaltsmaßen. DGKJ-konform für jede Phase, von Muttermilch bis zum mäkeligen Kleinkind.',
      visual: <NutritionPreview />,
    },
  ];

  return (
    <section className="relative bg-white/60 border-y border-primary-50">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Drei Bereiche, eine App
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Nicht nur Chat.
            <br />
            <span className="italic text-primary">Ein ganzer Begleiter.</span>
          </h2>
        </div>

        <div className="space-y-12">
          {surfaces.map((s, i) => (
            <div
              key={s.title}
              className={
                'grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ' +
                (i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : '')
              }
            >
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
                  {s.eyebrow}
                </div>
                <h3 className="font-display text-3xl font-semibold text-slate tracking-tight leading-snug">
                  {s.title}
                </h3>
                <p className="mt-4 text-base text-slate-muted leading-relaxed">{s.body}</p>
              </div>
              <div>{s.visual}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChatPreview() {
  return (
    <div className="rounded-2xl border border-primary-100 bg-cream p-5 shadow-sm">
      <div className="flex justify-end mb-3">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-primary text-white px-3.5 py-2 text-[13px]">
          Wann hört die 4-Monats-Schlafregression wirklich auf?
        </div>
      </div>
      <div className="flex justify-start">
        <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white border border-primary-50 shadow-sm px-3.5 py-2.5 text-[13px] leading-relaxed text-slate">
          Bei den meisten Babys 2–6 Wochen. Die „Regression" ist eigentlich dauerhaft —
          die Schlafzyklen reifen und bleiben so — aber das Chaos legt sich, sobald dein
          Baby lernt, Zyklen zu verbinden. Der Schlaf selbst geht nie wieder in den
          Neugeborenen-Modus zurück. Das Chaos schon.
        </div>
      </div>
    </div>
  );
}

function CardPreview() {
  return (
    <div className="rounded-2xl border border-primary-100 bg-white p-5 shadow-sm relative overflow-hidden">
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary-300"
      />
      <div className="pl-2">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center text-lg">
            🌱
          </div>
          <div className="text-[10px] uppercase tracking-wide font-medium text-slate-muted">
            Meilenstein · 14 Monate
          </div>
        </div>
        <h4 className="font-display text-lg font-semibold text-slate leading-snug">
          Erste Backenzähne — die härteste Zahnungs-Phase
        </h4>
        <p className="mt-2 text-[13px] text-slate leading-relaxed">
          Die ersten Backenzähne kommen meist zwischen 12 und 18 Monaten. Die meisten
          Eltern beschreiben sie als die schwerste Runde — größere Fläche, mehr Druck
          aufs Zahnfleisch, ein paar Tage Elend, während jeder Zahn durchbricht.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-[11px] text-primary-700 font-medium">Alma dazu fragen</span>
          <span className="text-slate-muted/40">·</span>
          <span className="text-[11px] text-slate-muted">Gelesen markieren</span>
        </div>
      </div>
    </div>
  );
}

function NutritionPreview() {
  return (
    <div className="rounded-2xl bg-gradient-to-br from-accent/15 to-accent/5 border border-accent/30 p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">🌿</span>
        <span className="text-[11px] font-semibold text-accent-600 uppercase tracking-wider">
          Dienstag · 12–18 Monate
        </span>
      </div>
      <h4 className="font-display text-lg font-semibold text-slate mt-1 leading-snug">
        Mini-Frittata-Förmchen
      </h4>
      <p className="mt-2 text-[13px] text-slate leading-relaxed">
        4 Eier · 60 ml Vollmilch · 40 g Spinat · 60 g Zucchini · 40 g Cheddar. Bei
        175 °C für 20 Min backen. Jedes Förmchen ist eine Mahlzeit für sich. Lässt sich
        2 Monate einfrieren.
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="text-[10px] font-medium bg-accent/10 text-accent-600 border border-accent/20 px-1.5 py-0.5 rounded">
          Ei
        </span>
        <span className="text-[10px] font-medium bg-accent/10 text-accent-600 border border-accent/20 px-1.5 py-0.5 rounded">
          Milch
        </span>
      </div>
    </div>
  );
}

function Pricing({ isAuthed }: { isAuthed: boolean }) {
  const prices = getPriceDisplay();
  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Einfache Preise
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Kostenlos starten.
            <br />
            <span className="italic text-primary">{prices.monthly}/Monat, wenn du mehr willst.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <PricingCard
            name="Free"
            price={prices.monthly === '$9.99' ? '$0' : prices.monthly === '€9,99' ? '€0' : 'CHF 0'}
            period="für immer"
            tagline="Perfekt, um Alma kennenzulernen."
            features={[
              '3 Fragen zum Reinschnuppern — ohne Anmeldung',
              '20 Fragen pro Monat nach der Anmeldung',
              'Voller Begleiter für ein Baby',
              'Alters-synchrone Erinnerungen',
            ]}
            cta={isAuthed ? 'Weiter' : 'Kostenlos starten'}
            ctaLink={isAuthed ? '/today' : '/start'}
            highlight={false}
          />
          <PricingCard
            name="Pro"
            price={prices.monthly}
            period="pro Monat"
            tagline="Für die Fragen, die nicht warten können."
            features={[
              'Unbegrenzte Fragen',
              'Bis zu 3 Baby-Profile',
              '90 Tage Gesprächsverlauf',
              'Sprache & Bilder (bald)',
            ]}
            cta="Pro holen"
            ctaLink={isAuthed ? '/pricing' : '/signup?next=/pricing'}
            highlight={true}
          />
        </div>

        <p className="mt-8 text-center text-sm text-slate-muted">
          {prices.annualSavings}
        </p>
      </div>
    </section>
  );
}

function PricingCard({
  name,
  price,
  period,
  tagline,
  features,
  cta,
  ctaLink,
  highlight,
}: {
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlight: boolean;
}) {
  return (
    <div
      className={
        'rounded-2xl p-7 ' +
        (highlight
          ? 'bg-slate text-white shadow-lg ring-1 ring-slate'
          : 'bg-white border border-primary-100')
      }
    >
      {highlight && (
        <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent/20 text-accent-300 text-[11px] font-semibold uppercase tracking-wider mb-4">
          Am beliebtesten
        </div>
      )}
      <div className={'text-sm font-medium ' + (highlight ? 'text-white/70' : 'text-slate-muted')}>
        {name}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className={'font-display text-5xl font-semibold ' + (highlight ? 'text-white' : 'text-slate')}>
          {price}
        </div>
        <div className={'text-sm ' + (highlight ? 'text-white/60' : 'text-slate-muted')}>{period}</div>
      </div>
      <p className={'mt-2 text-sm ' + (highlight ? 'text-white/70' : 'text-slate-muted')}>
        {tagline}
      </p>

      <ul className="mt-6 space-y-3">
        {features.map((f) => (
          <li
            key={f}
            className={'flex items-start gap-2.5 text-sm ' + (highlight ? 'text-white/90' : 'text-slate')}
          >
            <span className={highlight ? 'text-accent-300' : 'text-primary'}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to={ctaLink}
        className={
          'mt-7 block w-full text-center rounded-xl py-3 font-medium text-sm transition-colors ' +
          (highlight
            ? 'bg-white text-slate hover:bg-cream'
            : 'bg-primary text-white hover:bg-primary-600')
        }
      >
        {cta} →
      </Link>
    </div>
  );
}

function FAQ() {
  const items = [
    {
      q: 'Ist das ein Ersatz für meine Kinderärztin?',
      a: 'Nein, und wir geben uns Mühe, niemals so zu tun. Alma stellt keine Diagnosen. Jede Gesundheits-Antwort endet mit „Das ist allgemeine Orientierung, kein medizinischer Rat." Für alles Dringende verweist Alma auf deine Kinderärztin, den ärztlichen Bereitschaftsdienst (116 117) oder die 112. Wir arbeiten mit deiner Kinderärztin, nicht gegen sie.',
    },
    {
      q: 'Wie unterscheidet sich das von Googeln?',
      a: 'Google gibt dir 50 Artikel für jedes Alter, von jedem Autor, mit jeder Agenda. Alma gibt dir eine Antwort, abgestimmt auf das genaue Alter deines Babys, basierend auf DGKJ-, STIKO- und WHO-Empfehlungen, in normaler Sprache. Und es treibt dich nicht in Panik-Modus wie Google.',
    },
    {
      q: 'Wie geht Alma mit echten Notfällen um?',
      a: 'Fest verdrahtete Sicherheitsregeln. Fieber ab 38,0 °C bei einem Baby unter 3 Monaten → automatisch Notfall-Stufe, rotes Banner, „Ruf 112 oder fahr in die nächste Notaufnahme." Erwähnungen von Selbstgefährdung → Hinweis auf Telefonseelsorge und Wochenbett-Beratungsstellen, mit einer behutsamen Nachfrage. Diese Antworten hängen nie davon ab, dass die KI es richtig macht — sie sind regelbasiert.',
    },
    {
      q: 'Sind meine Daten geschützt?',
      a: 'Deine Nachrichten werden nur in deinem Konto bei Supabase gespeichert (verschlüsselt gespeichert, verschlüsselt übertragen). Wir verkaufen keine Daten. Wir teilen nichts mit Werbenetzwerken. Nachrichteninhalte gehen nie an Analyse-Plattformen. Du möchtest alles gelöscht haben? Schreib an hello@alma.baby, innerhalb von 30 Tagen ist alles weg.',
    },
    {
      q: 'Was ist, wenn mein Baby älter als 24 Monate ist?',
      a: 'Alma konzentriert sich aktuell auf die ersten 24 Monate — dort, wo pädiatrische Begleitung am wichtigsten ist und Eltern sie am meisten brauchen. Über 2 Jahren hinaus verändern sich die Fragen stark und wir wollen nichts versprechen, was wir nicht halten können. Melde dich trotzdem an — wenn wir den Begleiter erweitern, erfährst du es zuerst.',
    },
    {
      q: 'Kann ich Alma mit meinem Partner oder meiner Familie teilen?',
      a: 'Familien-Pläne kommen bald. Aktuell können beide Eltern dasselbe Konto von jedem Gerät aus nutzen — dein Konto, dein Baby, deine Daten, von euch beiden zugänglich.',
    },
  ];

  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section className="relative bg-white/60 border-y border-primary-50">
      <div className="mx-auto max-w-3xl px-6 py-20 sm:py-24">
        <div className="text-center mb-12">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
            Fragen
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
            Was Eltern <span className="italic text-primary">wirklich fragen.</span>
          </h2>
        </div>

        <div className="space-y-3">
          {items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div
                key={i}
                className={
                  'rounded-xl border transition-colors ' +
                  (isOpen ? 'border-primary-200 bg-white shadow-sm' : 'border-primary-50 bg-white/50 hover:bg-white')
                }
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                  aria-expanded={isOpen}
                >
                  <span className="font-medium text-slate text-[15px]">{item.q}</span>
                  <span
                    className={
                      'shrink-0 text-slate-muted transition-transform ' + (isOpen ? 'rotate-45' : '')
                    }
                    aria-hidden
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 text-[14px] text-slate-muted leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(50rem 30rem at 50% 50%, rgba(232,164,160,0.2) 0%, transparent 60%)',
        }}
      />
      <div className="relative mx-auto max-w-3xl px-6 py-20 sm:py-28 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-semibold text-slate tracking-tight leading-tight">
          Hör auf, um 3 Uhr zu googeln.
          <br />
          <span className="italic text-primary">Fang an, wieder zu schlafen.</span>
        </h2>
        <p className="mt-5 text-base text-slate-muted leading-relaxed max-w-xl mx-auto">
          Keine Anmeldung zum Starten. Zwei Fragen, um zu beginnen. Die ersten drei
          sind kostenlos.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          {isAuthed ? (
            <Link to="/today" className="btn-primary text-base px-8 py-3.5">
              Weiter zu deinem Tag →
            </Link>
          ) : (
            <Link to="/start" className="btn-primary text-base px-8 py-3.5 shadow-sm hover:shadow-md transition-shadow">
              Alma kostenlos fragen →
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
